import {computed} from "vue";

// A tracker reports two voltage channels (voltage1, voltage2) and the SKU row is the
// mapping layer: it says what each channel is and which one the backend shows to users
// and alerts on. battery1_status/battery2_status are that selector, not "this battery
// exists" flags, so exactly one is ever on. Everything downstream reads them as an
// if/else-if, which means both-on silently resolves to channel 1 and both-off produces
// no reading at all. Shared by the SKU create and edit dialogs so the two forms cannot
// drift apart on semantics that already caused one production incident.

// Firmware ip_message.c maps the channels per SKU: on every board except Nano voltage1
// is the internal backup (super cap) and voltage2 the external / vehicle rail; the Nano
// build swaps them, so voltage1 is its Li-ion cell. The hints state both cases rather
// than asserting one, because the operator's own labels are what carry the meaning.
export const BATTERY1_HINT = 'voltage1 as sent by the device. Super cap / internal backup on every SKU except Nano, where it is the Li-ion cell.';
export const BATTERY2_HINT = 'voltage2 as sent by the device. External / vehicle power on every SKU except Nano, where it is the backup.';

const POWER_SOURCE_EXTERNAL = 3;
const POWER_SOURCE_SUPER_CAP = 4;

// Rough split between a cell/super-cap rail and a vehicle rail. Deliberately loose: it
// only has to catch a full 12V window typed against a super cap channel (and the
// reverse), which is exactly how the thresholds were once entered inverted.
const LOW_VOLTAGE_CEILING = 5;

export function useSkuBatteryChannel(formData) {
    const channelLabel = (channel) => {
        const typed = formData[`battery${channel}_label`];
        const base = `Battery ${channel} (voltage${channel})`;
        return typed ? `${base}: ${typed}` : base;
    };

    const channel1Label = computed(() => channelLabel(1));
    const channel2Label = computed(() => channelLabel(2));

    const batteryDisplayChannel = computed({
        // Mirrors the precedence every consumer uses, so an existing row that has both
        // flags on preselects the channel that is actually being displayed today. The
        // getter never writes back: opening the dialog must not quietly repair a row.
        get() {
            if (formData.battery1_status) return 1;
            if (formData.battery2_status) return 2;
            return null;
        },
        set(channel) {
            formData.battery1_status = channel === 1;
            formData.battery2_status = channel === 2;
        },
    });

    // Surfaced instead of auto-corrected, so a legacy both-on / both-off row is visible
    // to the operator and only changes when they pick a channel themselves.
    const batteryChannelWarning = computed(() => {
        if (formData.battery1_status && formData.battery2_status) {
            return 'Both channels are marked for display. Only battery 1 is actually used. Pick one channel to correct this SKU.';
        }
        if (!formData.battery1_status && !formData.battery2_status) {
            return 'No channel is selected, so users get no battery reading for this SKU. Pick one.';
        }
        return null;
    });

    const thresholdWarnings = computed(() => {
        const warnings = [];
        [1, 2].forEach((channel) => {
            // The API can hand these back as numeric strings, so compare on numbers.
            const type = Number(formData[`battery${channel}_type`]);
            const full = parseFloat(formData[`battery${channel}_fully_charge_threshold`]);
            if (Number.isNaN(full)) return;
            if (type === POWER_SOURCE_SUPER_CAP && full > LOW_VOLTAGE_CEILING) {
                warnings.push(`Battery ${channel} is a Super Capacitor but its full charge threshold is ${full}V. Super caps sit near 2-3V, so a 12V window here usually means the vehicle rail was entered against the wrong channel.`);
            }
            if (type === POWER_SOURCE_EXTERNAL && full < LOW_VOLTAGE_CEILING) {
                warnings.push(`Battery ${channel} is External Power but its full charge threshold is ${full}V. A vehicle rail is normally 12V or more.`);
            }
        });
        return warnings;
    });

    return {
        channel1Label,
        channel2Label,
        batteryDisplayChannel,
        batteryChannelWarning,
        thresholdWarnings,
        battery1Hint: BATTERY1_HINT,
        battery2Hint: BATTERY2_HINT,
    };
}
